/* "Atualizar dados": o botão que força a releitura do Supabase para os agentes.
 *
 * Os cinco endpoints dos agentes (relatórios, telecom, seguros, saúde, assinaturas)
 * são servidos por
 * um cache de 12 h no servidor — as queries usam `staleTime: Infinity` e NUNCA
 * revalidam sozinhas. Este botão é o único caminho para dado mais novo que o TTL:
 * um POST manda o servidor reler o Supabase e regravar o cache; a invalidação em
 * seguida faz as queries relerem através do cache já quente (barato, local).
 *
 * Fica dentro de cada seção, não no topbar: refresca exatamente o dado ao lado
 * dele, e mostra de quando esse dado é. Sem estado próprio — quem monta e desmonta
 * (as chips do Margin Finder) não custa nada.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import type { AgentsRefreshPayload } from "../lib/api";
import { fmtDateTime } from "../lib/format";

export function AgentsRefresh({ fetchedAt, stale }: { fetchedAt?: string; stale?: boolean }) {
  const client = useQueryClient();

  const refresh = useMutation({
    mutationFn: () => api<AgentsRefreshPayload>("/api/agents/refresh", { json: {} }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["reports-latest"] });
      client.invalidateQueries({ queryKey: ["margin"] });
    },
  });

  const partial = refresh.data && !refresh.data.ok
    ? Object.values(refresh.data.results).find((r) => !r.ok)?.error
    : null;

  return (
    <>
      <button className="btn" disabled={refresh.isPending}
              onClick={() => refresh.mutate()}>
        {refresh.isPending ? "atualizando…" : "Atualizar dados"}
      </button>
      {fetchedAt && (
        <span className="mono" style={{ fontSize: 12, color: "var(--ink-3)" }}>
          dados de {fmtDateTime(fetchedAt)}
        </span>
      )}
      {/* .controls é flex com align-items:center; um .banner solto ali vira item
          encolhido e desalinhado na mesma linha do botão. flex-basis:100% quebra
          para a linha de baixo ocupando a largura toda, como os banners fora de
          .controls fazem por serem block-level. */}
      {stale && (
        <div style={{ flexBasis: "100%" }}>
          <div className="banner" role="status" style={{ marginBottom: 0 }}>
            <span aria-hidden>⚠</span>
            <span>Supabase indisponível — mostrando a última leitura.</span>
          </div>
        </div>
      )}
      {refresh.error && (
        <div style={{ flexBasis: "100%" }}>
          <div className="banner" role="alert" style={{ marginBottom: 0 }}>
            <span aria-hidden>⚠</span>
            <span>
              {refresh.error instanceof ApiError
                ? refresh.error.message : String(refresh.error)}
            </span>
          </div>
        </div>
      )}
      {partial && (
        <div style={{ flexBasis: "100%" }}>
          <div className="banner" role="alert" style={{ marginBottom: 0 }}>
            <span aria-hidden>⚠</span>
            <span>Parte dos dados não atualizou: {partial}</span>
          </div>
        </div>
      )}
    </>
  );
}
